/**
 * CashFlowDetailBar
 *
 * Stats strip below the expanded cash flow chart.
 * Two-row layout for award/IDV selections: identity row + stats row.
 * Compact inline format for aggregate view.
 */
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { parseStableDateInput } from '@/helpers/ssr/dates'

import type { AwardPortfolioItem } from '@/types/usaspending'

import {
  OUTLAY_REPORTING_START,
  formatCurrency,
  safeNum,
} from '../../charts/cashflow-utils'
import { ASSISTANCE_CATEGORIES, type IdvGroup } from '../../charts/idv-grouping'
import { Stat } from './award-sidebar/atoms'

export type DetailSelection =
  | {
      type: 'aggregate'
      totalCeiling: number
      totalObligation: number
      totalOutlay: number
      awardCount: number
    }
  | { type: 'award'; award: AwardPortfolioItem }
  | { type: 'idv'; group: IdvGroup }

function pct(a: number, b: number): string {
  if (b === 0) return '\u2014'
  return ((a / b) * 100).toFixed(1) + '%'
}

function Warnings({
  hasPreReportingData,
  excludedIdvTypes,
}: {
  hasPreReportingData?: boolean
  excludedIdvTypes?: string[]
}) {
  if (
    !hasPreReportingData &&
    (!excludedIdvTypes || excludedIdvTypes.length === 0)
  )
    return null
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 pt-0.5">
      {hasPreReportingData && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help whitespace-nowrap text-xs text-amber-600/70 underline decoration-dotted decoration-amber-400/40 dark:text-amber-400/70 dark:decoration-amber-300/30">
              Pre-FY2022 disbursement estimated
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            inverseColors
            className="max-w-xs space-y-1.5 px-3 py-2 text-xs"
          >
            <p>
              Prior to FY2022, award-level outlay (disbursement) data was not
              required in monthly DATA Act submissions. Pre-FY2022 figures shown
              here are estimated from obligation data and the agency&apos;s
              outlay-to-obligation ratio.
            </p>
            <p>
              Beginning in FY2022, DAIMS v2.1 required all agencies to report
              File C award outlays monthly.{' '}
              <a
                href="https://wwwkc.fiscal.treasury.gov/files/data-transparency/daims-release-notes-v2.1.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                DAIMS v2.1 Release Notes →
              </a>
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      {excludedIdvTypes && excludedIdvTypes.length > 0 && (
        <span className="whitespace-nowrap text-xs text-primary-300 dark:text-primary-600">
          {excludedIdvTypes.join(' & ')} vehicles excluded
        </span>
      )}
    </div>
  )
}

export function CashFlowDetailBar({
  selection,
  referenceNowMs,
  hasPreReportingData,
  excludedIdvTypes,
}: {
  selection: DetailSelection
  referenceNowMs: number | null
  hasPreReportingData?: boolean
  excludedIdvTypes?: string[]
}) {
  return (
    <div className="space-y-1 border-t border-primary-100 px-3 py-2 text-xs dark:border-primary-800 sm:px-4">
      {selection.type === 'aggregate' ? (
        <AggregateDetailContent
          totalCeiling={selection.totalCeiling}
          totalObligation={selection.totalObligation}
          totalOutlay={selection.totalOutlay}
          awardCount={selection.awardCount}
        />
      ) : selection.type === 'award' ? (
        <AwardDetailContent award={selection.award} />
      ) : (
        <IdvDetailContent
          group={selection.group}
          referenceNowMs={referenceNowMs}
        />
      )}
      <Warnings
        hasPreReportingData={hasPreReportingData}
        excludedIdvTypes={excludedIdvTypes}
      />
    </div>
  )
}

function AggregateDetailContent({
  totalCeiling,
  totalObligation,
  totalOutlay,
  awardCount,
}: {
  totalCeiling: number
  totalObligation: number
  totalOutlay: number
  awardCount: number
}) {
  return (
    <>
      {/* Row 1: Identity */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-primary-800 dark:text-primary-100">
          All Awards
        </span>
        <span className="rounded bg-primary-100 px-1.5 py-0.5 text-xs font-semibold text-primary-500 dark:bg-primary-800 dark:text-primary-400">
          {awardCount} awards
        </span>
      </div>

      {/* Row 2: Stats */}
      <div className="flex flex-wrap items-center gap-1.5">
        {totalCeiling > 0 && (
          <Stat label="Ceiling">{formatCurrency(totalCeiling)}</Stat>
        )}
        <Stat label="Obl" color="#3b82f6">
          {formatCurrency(totalObligation)}
          {totalCeiling > 0 && (
            <span className="font-normal text-primary-400 dark:text-primary-500">
              {' '}
              ({pct(totalObligation, totalCeiling)})
            </span>
          )}
        </Stat>
        <Stat label="Disb" color="#22c55e">
          {formatCurrency(totalOutlay)}
          <span className="font-normal text-primary-400 dark:text-primary-500">
            {' '}
            ({pct(totalOutlay, totalObligation)})
          </span>
        </Stat>
      </div>
    </>
  )
}

function AwardDetailContent({ award: a }: { award: AwardPortfolioItem }) {
  const ceiling = safeNum(a.base_and_all_options_value) || null
  const obl = safeNum(a.total_obligation)
  const out = safeNum(a.total_outlays)
  const isAssistance =
    a.category != null && ASSISTANCE_CATEGORIES.has(a.category)
  const startDate = parseStableDateInput(a.period_of_performance_start_date)
  const predatesReporting =
    startDate != null && startDate < OUTLAY_REPORTING_START
  const agency = a.awarding_toptier_agency_name ?? null

  return (
    <>
      {/* Row 1: Identity */}
      <div className="flex items-center gap-2">
        <span
          className="font-mono text-sm font-bold"
          style={{ color: '#3b82f6' }}
        >
          {a.display_award_id ?? a.generated_unique_award_id}
        </span>
        <span className="rounded bg-primary-100 px-1.5 py-0.5 text-xs font-semibold text-primary-500 dark:bg-primary-800 dark:text-primary-400">
          {isAssistance ? 'Grant' : 'Contract'}
        </span>
        {agency && (
          <span className="truncate text-primary-400 dark:text-primary-500">
            {agency}
          </span>
        )}
      </div>

      {/* Row 2: Stats */}
      <div className="flex flex-wrap items-center gap-1.5">
        {ceiling != null && (
          <Stat label="Ceiling">{formatCurrency(ceiling)}</Stat>
        )}
        <Stat label="Obl" color="#3b82f6">
          {formatCurrency(obl)}
          {ceiling != null && ceiling > 0 && (
            <span className="font-normal text-primary-400 dark:text-primary-500">
              {' '}
              ({pct(obl, ceiling)})
            </span>
          )}
        </Stat>
        <Stat
          label="Disb"
          color="#22c55e"
          title={
            predatesReporting
              ? 'Disbursement data may be incomplete — award predates FY2022 reporting'
              : undefined
          }
        >
          {formatCurrency(out)}
          <span className="font-normal text-primary-400 dark:text-primary-500">
            {' '}
            ({pct(out, obl)})
          </span>
        </Stat>
        {a.period_of_performance_start_date &&
          a.period_of_performance_current_end_date && (
            <Stat label="PoP">
              {a.period_of_performance_start_date} {'\u2192'}{' '}
              {a.period_of_performance_current_end_date}
            </Stat>
          )}
      </div>
    </>
  )
}

function IdvDetailContent({
  group,
  referenceNowMs,
}: {
  group: IdvGroup
  referenceNowMs: number | null
}) {
  const vehicleCeiling = safeNum(group.parentAward?.base_and_all_options_value)
  const agency =
    group.parentAward?.awarding_toptier_agency_name ??
    group.children[0]?.awarding_toptier_agency_name ??
    null
  const orderingEnd = group.parentAward?.ordering_period_end_date
  const orderingEndDate = parseStableDateInput(orderingEnd)
  const orderingOpen =
    referenceNowMs == null || orderingEndDate == null
      ? null
      : orderingEndDate.getTime() >= referenceNowMs

  return (
    <>
      {/* Row 1: Identity */}
      <div className="flex items-center gap-2">
        <span
          className="font-mono text-sm font-bold"
          style={{ color: '#a855f7' }}
        >
          {group.parentPiid}
        </span>
        <span className="rounded bg-primary-100 px-1.5 py-0.5 text-xs font-semibold text-primary-500 dark:bg-primary-800 dark:text-primary-400">
          {group.typeLabel}
        </span>
        <span className="text-xs text-primary-400 dark:text-primary-500">
          {group.children.length} TOs
        </span>
        {agency && (
          <span className="truncate text-primary-400 dark:text-primary-500">
            {agency}
          </span>
        )}
      </div>

      {/* Row 2: Stats */}
      <div className="flex flex-wrap items-center gap-1.5">
        {vehicleCeiling > 0 && (
          <Stat label="Vehicle">{formatCurrency(vehicleCeiling)}</Stat>
        )}
        <Stat label="Ceiling">{formatCurrency(group.recipientCeiling)}</Stat>
        <Stat label="Obl" color="#3b82f6">
          {formatCurrency(group.recipientObligation)}
          {group.recipientCeiling > 0 && (
            <span className="font-normal text-primary-400 dark:text-primary-500">
              {' '}
              ({pct(group.recipientObligation, group.recipientCeiling)})
            </span>
          )}
        </Stat>
        {!group.isShared && vehicleCeiling > 0 && (
          <Stat label="Capture" color="#f59e0b">
            {pct(group.recipientObligation, vehicleCeiling)}
          </Stat>
        )}
        {orderingEnd && (
          <Stat
            label="Ordering Ends"
            color={
              orderingOpen == null
                ? undefined
                : orderingOpen
                  ? '#22c55e'
                  : '#ef4444'
            }
          >
            {orderingEnd}
          </Stat>
        )}
      </div>
    </>
  )
}
